import React, { useState } from 'react';
import { 
  Plus, Trash2, Upload, Camera, DollarSign, FileText, 
  Clock, CheckCircle2, XCircle, AlertCircle, ChevronRight,
  Image, Paperclip, Send, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

// Mock data for preview
const mockChangeOrders = [
  {
    id: '1',
    title: 'Additional Electrical Outlets in Kitchen',
    description: 'Client requested 4 additional outlets on the island and 2 near the pantry for appliances.',
    total_amount: 1250.00,
    status: 'pending',
    submitted_date: '2026-04-08',
    contractor_name: 'Electrical Concepts',
    line_items: [
      { description: '6x 20A outlets with GFCI', quantity: 6, unit_price: 85, total: 510 },
      { description: 'Wiring and conduit', quantity: 1, unit_price: 340, total: 340 },
      { description: 'Labor (4 hours)', quantity: 4, unit_price: 100, total: 400 },
    ],
    photos: ['/api/placeholder/400/300']
  },
  {
    id: '2',
    title: 'Upgraded Bathroom Tile',
    description: 'Upgrade from standard ceramic to porcelain tile per client request.',
    total_amount: 875.00,
    status: 'approved',
    submitted_date: '2026-04-05',
    approved_date: '2026-04-06',
    contractor_name: 'Graves Flooring',
    line_items: [
      { description: 'Porcelain tile upgrade (price difference)', quantity: 120, unit_price: 4.50, total: 540 },
      { description: 'Additional labor for porcelain', quantity: 2.5, unit_price: 134, total: 335 },
    ]
  },
  {
    id: '3',
    title: 'Move HVAC Return Vent',
    description: 'Relocate return vent from hallway to living room ceiling.',
    total_amount: 425.00,
    status: 'rejected',
    submitted_date: '2026-04-02',
    rejected_date: '2026-04-03',
    rejection_reason: 'Not in budget. Will address in Phase 2.',
    contractor_name: 'Kinzler'
  }
];

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-700 border-red-200'
  };
  const icons = {
    pending: <Clock className="w-3 h-3" />,
    approved: <CheckCircle2 className="w-3 h-3" />,
    rejected: <XCircle className="w-3 h-3" />
  };
  return (
    <Badge variant="outline" className={`${styles[status]} flex items-center gap-1`}>
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

// ============================================
// CONTRACTOR VIEW - Submit Change Order Form
// ============================================
const ContractorSubmitForm = () => {
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unit_price: 0 }
  ]);
  
  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0 }]);
  };
  
  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };
  
  const total = lineItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-8 bg-slate-50 min-h-screen">
      <div className="mb-6">
        <button className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Portal
        </button>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Submit Change Order</h1>
        <p className="text-slate-500 mt-1">Request additional work or scope changes for approval</p>
      </div>

      <Card className="shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]">
        <CardContent className="p-6 space-y-6">
          {/* Project & Phase Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-slate-500">Project</Label>
              <div className="mt-1 p-3 bg-slate-100 rounded-lg text-slate-700 font-medium">
                1615 38th St SE
              </div>
            </div>
            <div>
              <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-slate-500">Related Phase</Label>
              <select className="mt-1 w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent">
                <option>Phase 3: Rough Electrical</option>
                <option>Phase 4: Plumbing</option>
                <option>Phase 5: HVAC</option>
                <option>+ Create New Line Item</option>
              </select>
            </div>
          </div>

          {/* Title & Description */}
          <div>
            <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-slate-500">Change Order Title</Label>
            <Input 
              className="mt-1 focus:ring-2 focus:ring-blue-600" 
              placeholder="e.g., Additional Electrical Outlets in Kitchen"
            />
          </div>
          
          <div>
            <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-slate-500">Description of Work</Label>
            <Textarea 
              className="mt-1 min-h-[100px] focus:ring-2 focus:ring-blue-600" 
              placeholder="Describe the additional work needed and why..."
            />
          </div>

          {/* Line Items */}
          <div>
            <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-slate-500 mb-3 block">Cost Breakdown</Label>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-3 font-medium text-slate-600">Description</th>
                    <th className="text-center p-3 font-medium text-slate-600 w-20">Qty</th>
                    <th className="text-right p-3 font-medium text-slate-600 w-28">Unit Price</th>
                    <th className="text-right p-3 font-medium text-slate-600 w-28">Total</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, idx) => (
                    <tr key={idx} className="border-t border-slate-200">
                      <td className="p-2">
                        <Input 
                          className="border-0 bg-transparent focus:ring-0 p-1" 
                          placeholder="Item description"
                          value={item.description}
                          onChange={(e) => {
                            const updated = [...lineItems];
                            updated[idx].description = e.target.value;
                            setLineItems(updated);
                          }}
                        />
                      </td>
                      <td className="p-2">
                        <Input 
                          type="number" 
                          className="border-0 bg-transparent focus:ring-0 p-1 text-center w-full" 
                          value={item.quantity}
                          onChange={(e) => {
                            const updated = [...lineItems];
                            updated[idx].quantity = parseFloat(e.target.value) || 0;
                            setLineItems(updated);
                          }}
                        />
                      </td>
                      <td className="p-2">
                        <div className="flex items-center justify-end">
                          <span className="text-slate-400 mr-1">$</span>
                          <Input 
                            type="number" 
                            className="border-0 bg-transparent focus:ring-0 p-1 text-right w-20" 
                            value={item.unit_price}
                            onChange={(e) => {
                              const updated = [...lineItems];
                              updated[idx].unit_price = parseFloat(e.target.value) || 0;
                              setLineItems(updated);
                            }}
                          />
                        </div>
                      </td>
                      <td className="p-2 text-right font-medium text-slate-700 tabular-nums">
                        ${(item.quantity * item.unit_price).toLocaleString()}
                      </td>
                      <td className="p-2">
                        {lineItems.length > 1 && (
                          <button 
                            onClick={() => removeLineItem(idx)}
                            className="text-slate-400 hover:text-red-500 p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td colSpan="3" className="p-3">
                      <button 
                        onClick={addLineItem}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
                      >
                        <Plus className="w-4 h-4" /> Add Line Item
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <div className="text-slate-500 text-xs uppercase tracking-wide">Total</div>
                      <div className="text-xl font-bold text-slate-900 tabular-nums">${total.toLocaleString()}</div>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Photo Upload */}
          <div>
            <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-slate-500 mb-3 block">
              Photos & Documentation
            </Label>
            <div className="border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50 transition-colors rounded-lg p-8 text-center cursor-pointer">
              <div className="flex flex-col items-center gap-3">
                <div className="p-4 bg-slate-100 rounded-full">
                  <Camera className="w-8 h-8 text-slate-400" />
                </div>
                <div>
                  <p className="font-medium text-slate-700">Upload Photos or Documents</p>
                  <p className="text-sm text-slate-500 mt-1">Take photos showing the area or attach invoices/quotes</p>
                </div>
                <Button variant="outline" className="mt-2">
                  <Upload className="w-4 h-4 mr-2" /> Choose Files
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sticky Submit Bar */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-xl border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            <div className="text-sm text-slate-500">Change Order Total</div>
            <div className="text-2xl font-bold text-slate-900 tabular-nums">${total.toLocaleString()}</div>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700 px-8 py-6 text-lg shadow-[0_8px_24px_-4px_rgba(37,99,235,0.25)]">
            <Send className="w-5 h-5 mr-2" /> Submit for Approval
          </Button>
        </div>
      </div>
      
      {/* Spacer for sticky bar */}
      <div className="h-32"></div>
    </div>
  );
};

// ============================================
// OWNER VIEW - Change Orders List & Review
// ============================================
const OwnerChangeOrdersView = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const pendingCount = mockChangeOrders.filter(o => o.status === 'pending').length;

  return (
    <div className="p-6 md:p-8 bg-slate-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Change Orders</h1>
            <p className="text-slate-500 mt-1">Review and approve contractor scope changes</p>
          </div>
          {pendingCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-amber-700">{pendingCount} Pending Review</span>
            </div>
          )}
        </div>

        {/* Change Orders Table */}
        <Card className="shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-4 font-medium text-slate-600">Change Order</th>
                  <th className="text-left p-4 font-medium text-slate-600">Contractor</th>
                  <th className="text-right p-4 font-medium text-slate-600">Amount</th>
                  <th className="text-center p-4 font-medium text-slate-600">Status</th>
                  <th className="text-right p-4 font-medium text-slate-600">Submitted</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {mockChangeOrders.map((order) => (
                  <tr 
                    key={order.id} 
                    className={`border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${order.status === 'pending' ? 'bg-amber-50/30' : ''}`}
                    onClick={() => setSelectedOrder(order)}
                  >
                    <td className="p-4">
                      <div className="font-medium text-slate-900">{order.title}</div>
                      <div className="text-slate-500 text-xs mt-1 line-clamp-1">{order.description}</div>
                    </td>
                    <td className="p-4 text-slate-700">{order.contractor_name}</td>
                    <td className="p-4 text-right font-medium text-slate-900 tabular-nums">
                      ${order.total_amount.toLocaleString()}
                    </td>
                    <td className="p-4 text-center">
                      <StatusBadge status={order.status} />
                    </td>
                    <td className="p-4 text-right text-slate-500">{order.submitted_date}</td>
                    <td className="p-4">
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Review Modal Preview */}
        {selectedOrder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-[0_16px_48px_-12px_rgba(15,23,42,0.15)]">
              {/* Sticky Header */}
              <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 p-6 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{selectedOrder.title}</h2>
                  <p className="text-slate-500 text-sm mt-1">From {selectedOrder.contractor_name} • {selectedOrder.submitted_date}</p>
                </div>
                <div className="flex items-center gap-3">
                  {selectedOrder.status === 'pending' && (
                    <>
                      <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
                        <XCircle className="w-4 h-4 mr-2" /> Reject
                      </Button>
                      <Button className="bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle2 className="w-4 h-4 mr-2" /> Approve
                      </Button>
                    </>
                  )}
                  <Button variant="ghost" onClick={() => setSelectedOrder(null)}>
                    Close
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Main Content */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Description */}
                    <div>
                      <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-slate-500">Description</Label>
                      <p className="mt-2 text-slate-700">{selectedOrder.description}</p>
                    </div>

                    {/* Line Items */}
                    {selectedOrder.line_items && (
                      <div>
                        <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-slate-500">Cost Breakdown</Label>
                        <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-100">
                              <tr>
                                <th className="text-left p-3 font-medium text-slate-600">Item</th>
                                <th className="text-center p-3 font-medium text-slate-600">Qty</th>
                                <th className="text-right p-3 font-medium text-slate-600">Unit</th>
                                <th className="text-right p-3 font-medium text-slate-600">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedOrder.line_items.map((item, idx) => (
                                <tr key={idx} className="border-t border-slate-200">
                                  <td className="p-3 text-slate-700">{item.description}</td>
                                  <td className="p-3 text-center text-slate-600">{item.quantity}</td>
                                  <td className="p-3 text-right text-slate-600">${item.unit_price}</td>
                                  <td className="p-3 text-right font-medium text-slate-900 tabular-nums">${item.total.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                              <tr>
                                <td colSpan="3" className="p-3 text-right font-medium text-slate-600">Total</td>
                                <td className="p-3 text-right text-xl font-bold text-slate-900 tabular-nums">${selectedOrder.total_amount.toLocaleString()}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Rejection Reason */}
                    {selectedOrder.status === 'rejected' && selectedOrder.rejection_reason && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-red-600">Rejection Reason</Label>
                        <p className="mt-2 text-red-700">{selectedOrder.rejection_reason}</p>
                      </div>
                    )}
                  </div>

                  {/* Sidebar */}
                  <div className="space-y-6">
                    {/* Status */}
                    <Card className="p-4">
                      <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-slate-500">Status</Label>
                      <div className="mt-2">
                        <StatusBadge status={selectedOrder.status} />
                      </div>
                    </Card>

                    {/* Photos */}
                    {selectedOrder.photos && (
                      <Card className="p-4">
                        <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-slate-500">Attached Photos</Label>
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <div className="aspect-square bg-slate-200 rounded-lg flex items-center justify-center">
                            <Image className="w-8 h-8 text-slate-400" />
                          </div>
                          <div className="aspect-square bg-slate-200 rounded-lg flex items-center justify-center">
                            <Image className="w-8 h-8 text-slate-400" />
                          </div>
                        </div>
                      </Card>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// PREVIEW PAGE - Shows Both Views
// ============================================
export default function ChangeOrderPreview() {
  const [view, setView] = useState('contractor');

  return (
    <div className="min-h-screen bg-slate-900">
      {/* View Switcher */}
      <div className="sticky top-0 z-50 bg-slate-800 border-b border-slate-700 p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <h1 className="text-white font-bold text-lg">Change Order Feature Preview</h1>
          <div className="flex gap-2">
            <Button 
              variant={view === 'contractor' ? 'default' : 'outline'}
              onClick={() => setView('contractor')}
              className={view === 'contractor' ? 'bg-blue-600' : 'bg-transparent text-white border-slate-600'}
            >
              Contractor View
            </Button>
            <Button 
              variant={view === 'owner' ? 'default' : 'outline'}
              onClick={() => setView('owner')}
              className={view === 'owner' ? 'bg-blue-600' : 'bg-transparent text-white border-slate-600'}
            >
              Owner View
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      {view === 'contractor' ? <ContractorSubmitForm /> : <OwnerChangeOrdersView />}
    </div>
  );
}
